import { fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import { debounce, next, schedule } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { equal } from 'webapp/utils/common';
import { getWidthWithMargins } from 'webapp/utils/html';
import { formatNumber } from 'webapp/utils/number';

import didInsert from 'webapp/modifiers/did-insert';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonListLoadNextComponent from 'webapp/components/common/list/load-next';

import type { ItemTypeBase } from 'webapp/models-custom/filter-sort/base';
import type FilterSort from 'webapp/models-custom/filter-sort/base';

export interface LazyLoadItem extends ItemTypeBase {
	animationClass?: string;
}

type LoadNextFn = () => void;

// the parts of a filterSort the lazy load reads (unlike FilterSort itself, also fits a union of filterSorts)
export type LazyLoadFilterSort = Pick<
	FilterSort<ItemTypeBase>,
	'results' | 'selectors' | 'hideSort'
>;

// the items of a filterSort, or of each filterSort in a union (e.g. boards or reports)
export type LazyLoadFilterSortItem<FS extends LazyLoadFilterSort> = FS extends {
	results: (infer Item)[];
}
	? Item
	: never;

export interface Args<FS extends LazyLoadFilterSort = LazyLoadFilterSort> {
	filterSort: FS;
	filterExtraFn?: (results: LazyLoadFilterSortItem<FS>[]) => LazyLoadFilterSortItem<FS>[]; // Used to filter the results one more time. Useful for pages with one filterSort that is later split into multiple sections (e.g. favorite / other goals)
	animationProperty?: string; // If set, the visible items are not immidiately replaced with a newer version, but are kept for a few ms so an exiting animation can be applied. Similary the new items are assigned a temprorary animationClass so they can be animated into the view
	initialRowCount?: number; // If not provided, all items are loaded at once
	loadMoreRowCount?: number; // If not provided, next action is to show all remaining items at once
	initialReducedCount?: number; // If set, the initialItemsShown is offset by n, to leave space for buttons / actions
	classNames?: string;
	useScrollableContainer?: boolean; // In case we want to apply lazy load to some other scrollable container instead window
	scrollableContainerSelector?: string; // Selector of the scrollable container
	autoLoaded?: boolean; // If set, the next items are loaded automatically when the user scrolls to the bottom of the current container
	bottomDistance?: number; // Start loading the next batch of items when the user scrolls this many px close to the bottom of the item container
	onItemsPerRowCalculated?: (c: number) => void;
	checkHTMLNodeValidity?: () => void; // see utils/html:cleanupDuplicateNodes for more info
}

interface YieldedSettings {
	loadNext: (() => void) | null;
	itemsPerRow: number;
	isNextLoadFinal: boolean;
	areAllItemsLoaded: boolean;
	hideSort: boolean;
}

const Helper = {
	MIN_ITEM_WIDTH: 200,
	MIN_ITEMS_SHOWN_ON_INIT: 10,
};

// FS: the @filterSort given (inferred); ExtraArgs: the args a subclass adds
export interface CommonListLazyLoadComponentInterface<
	FS extends LazyLoadFilterSort,
	ExtraArgs extends object,
> {
	Args: Args<FS> & ExtraArgs;
	Blocks: {
		default: [items: (LazyLoadFilterSortItem<FS> & LazyLoadItem)[], settings: YieldedSettings];
	}; // this is needed for yield
}

// Subclasses that add args extend this one. Glint only infers FS from @filterSort when the invoked class has no
// other type parameter, hence the default export below.
export class CommonListLazyLoadBaseComponent<
	FS extends LazyLoadFilterSort,
	ExtraArgs extends object,
> extends Component<CommonListLazyLoadComponentInterface<FS, ExtraArgs>> {
	@tracked itemsPerRow = 0;
	@tracked initialItemsShown = 0;
	@tracked visibleItems: LazyLoadItem[] = [];
	@tracked visibleItemsCount = 0;

	retryUpdateGridOptions = true;
	scrollableContainerNode: HTMLElement | null = null;
	node: HTMLElement | null = null;
	loadNext: LoadNextFn | null = null;

	get rootSelector() {
		return this.args.filterSort.selectors?.root ?? 'ul';
	}

	get itemSelector() {
		return this.args.filterSort.selectors?.item ?? 'li';
	}

	get initialRowCount() {
		return this.args.initialRowCount || -1;
	}

	get loadAllImmidiatelly() {
		return this.initialRowCount <= 0;
	}

	get results(): LazyLoadFilterSortItem<FS>[] {
		const results = this.args.filterSort.results as LazyLoadFilterSortItem<FS>[];
		if (this.args.filterExtraFn) {
			return this.args.filterExtraFn(results);
		} else {
			return results;
		}
	}

	get itemsCount() {
		return this.results.length;
	}

	get hideSort() {
		return this.args.filterSort.hideSort || this.itemsCount < 2;
	}

	get itemsCountFormatted() {
		return formatNumber(this.itemsCount, '#,###', { scale: 0 });
	}

	get initialReducedCount() {
		return this.args.initialReducedCount || 0;
	}

	get bottomDistance() {
		return this.args.bottomDistance || 0;
	}

	get isLoadTypeGradual() {
		return typeof this.args.loadMoreRowCount === 'number';
	}

	get nextItemsShown() {
		const nextItemsShown = this.isLoadTypeGradual
			? this.args.loadMoreRowCount! * this.itemsPerRow
			: this.itemsCount - this.initialItemsShown;

		return nextItemsShown;
	}

	get isNextLoadFinal() {
		return this.itemsCount <= this.nextItemsShown + this.visibleItemsCount;
	}

	get areAllItemsLoaded() {
		return this.itemsCount === this.visibleItemsCount;
	}

	get classNames() {
		return this.args.classNames || '';
	}

	// visibleItems are the filterSort's items, which load-next only knows as LazyLoadItem
	get yieldedItems(): (LazyLoadFilterSortItem<FS> & LazyLoadItem)[] {
		return this.visibleItems as (LazyLoadFilterSortItem<FS> & LazyLoadItem)[];
	}

	onItemsPerRowCalculated(itemsPerRow: number) {
		this.itemsPerRow = itemsPerRow;

		if (this.args.onItemsPerRowCalculated) {
			this.args.onItemsPerRowCalculated(this.itemsPerRow);
		}
	}

	@action
	updateGridOptions() {
		const node = this.node,
			rowWidthInPx = node ? parseFloat(window.getComputedStyle(node).width) : 0;

		if (this.itemsPerRow === 0) {
			/*
				Add some items in DOM to try to calculate item width

				- if rowWidthInPx is a number and
					- rowWidthInPx < Helper.MIN_ITEM_WIDTH set to at least 1
					- otherwise, load at least one row worth of items
				- if rowWidthInPx not a number (example: element is hidden) set to 10 and expect next calls to updateGridOptions to find the right value
			*/
			this.itemsPerRow =
				Math.floor(rowWidthInPx / Helper.MIN_ITEM_WIDTH) ||
				(isNaN(rowWidthInPx) ? Helper.MIN_ITEMS_SHOWN_ON_INIT : 1);

			schedule('afterRender', () => {
				next(this, () => {
					this.updateGridOptions();
				});
			});
		} else {
			const itemNode = this.node?.querySelector(this.itemSelector); // Do not use this.args.filterSort.itemDomSelector - .flex-item--placeholder or .flex-item--fake are valid for item width calculation

			if (itemNode) {
				const itemWidth = getWidthWithMargins(itemNode as HTMLElement),
					itemsPerRow =
						itemWidth === 0 ? NaN : Math.floor((rowWidthInPx + 1) / itemWidth);

				if (!isNaN(itemsPerRow)) {
					// Realistic items per row number calculated
					this.onItemsPerRowCalculated(Math.floor((rowWidthInPx + 1) / itemWidth) || 1);
				} else if (this.retryUpdateGridOptions) {
					setTimeout(() => {
						// Templates have width auto => https://stackoverflow.com/q/8628005
						this.updateGridOptions();
					});
				} else {
					// item is found, but itemWidth is 0, and we don't divide by 0. Example, first tr in table views is in thead which is hidden on mobile res
					this.onItemsPerRowCalculated(1);
				}
			}

			this.retryUpdateGridOptions = false;
		}

		this.initialItemsShown = this.loadAllImmidiatelly
			? this.itemsCount
			: Math.max(0, this.initialRowCount * this.itemsPerRow - this.initialReducedCount);
	}

	@action
	updateVisibleItems(visibleItems: LazyLoadItem[]) {
		if (
			this.visibleItems.length === 0 &&
			visibleItems.length > 0 &&
			!this.retryUpdateGridOptions
		) {
			next(() => {
				/*
					There were no items (in store) on setupView, so updateGridOptions set some defaults.
					Now we have real items, try to recalculate itemsPerRow correctly

					If updateVisibleItems is called from setupView, retryUpdateGridOptions is still true, so we skip it.
				 */
				this.updateGridOptions();
			});
		}

		this.visibleItemsCount = visibleItems.length;

		const aProp = this.args.animationProperty as keyof LazyLoadItem,
			autoLoad = () => {
				if (this.args.autoLoaded) {
					setTimeout(() => {
						this.autoLoad();
					});
				}
			};

		if (aProp) {
			const newItems = visibleItems.map((item) => item[aProp]),
				oldItems = this.visibleItems.map((item) => item[aProp]),
				removedItems = this.visibleItems.filter((item) => !newItems.includes(item[aProp]));

			const shouldAnimate = !equal(oldItems, newItems); // Could happen if the same order is returned after differnt sort

			if (shouldAnimate) {
				removedItems.forEach((item) => {
					item.animationClass = 'item-out';
				});
			}

			setTimeout(
				() => {
					this.visibleItems = visibleItems.map((item) => {
						if (shouldAnimate) {
							// add animation class completely new items
							if (!oldItems.includes(item[aProp])) {
								item.animationClass = 'item-in';
							}
						}

						return item;
					});

					setTimeout(() => {
						if (shouldAnimate) {
							// cleanup animation class
							removedItems.forEach((item) => {
								item.animationClass = '';
							});

							this.visibleItems.map((item) => {
								item.animationClass = '';
							});
						}

						// clean up duplicate nodes if configured
						if (this.args.checkHTMLNodeValidity) {
							this.args.checkHTMLNodeValidity();
						}

						autoLoad();
					}, 300);
				},
				this.visibleItems.length ? 200 : 0,
			);
		} else {
			this.visibleItems = visibleItems;

			// clean up duplicate nodes if configured
			if (this.args.checkHTMLNodeValidity) {
				this.args.checkHTMLNodeValidity();
			}

			autoLoad();
		}
	}

	@action
	setupViewWithLoadNext(loadNext: LoadNextFn, node: HTMLElement): void {
		const targetNode = node.querySelector<HTMLElement>(this.rootSelector);

		if (targetNode) {
			this.node = targetNode;
			this.updateGridOptions();
			this.loadNext = loadNext || null;

			if (this.args.useScrollableContainer || this.args.scrollableContainerSelector) {
				const scrollableContainerSelector = this.args.scrollableContainerSelector;
				this.scrollableContainerNode = scrollableContainerSelector
					? node.closest(scrollableContainerSelector)!
					: node;

				this.scrollableContainerNode?.addEventListener('scroll', this.debounceAutoLoad);
			} else {
				window.addEventListener('scroll', this.debounceAutoLoad);
			}
		}
	}

	@action
	teardownView() {
		if (this.args.useScrollableContainer) {
			this.scrollableContainerNode?.removeEventListener('scroll', this.debounceAutoLoad);
		} else {
			window.removeEventListener('scroll', this.debounceAutoLoad);
		}
	}

	@action
	debounceAutoLoad() {
		if (this.args.autoLoaded) {
			debounce(this, this.autoLoad, 200);
		}
	}

	@action
	autoLoad(): void {
		const targetNode = this.node,
			scrollableContainerNodeHeight =
				this.scrollableContainerNode?.clientHeight || window.innerHeight,
			isBottomThresholdVisible =
				targetNode &&
				scrollableContainerNodeHeight >=
					Math.max(targetNode.getBoundingClientRect().bottom - this.bottomDistance, 0);

		if (isBottomThresholdVisible && !this.areAllItemsLoaded) {
			this.loadNext?.();

			setTimeout(() => {
				this.autoLoad();
			}, 200); // Allow time for newly appended items to load in UI, so isBottomThresholdVisible isn't falsely true
		}
	}
	<template>
		<CommonListLoadNextComponent
			@allItems={{this.results}}
			@initialItemsShown={{this.initialItemsShown}}
			@nextItemsShown={{this.nextItemsShown}}
			@onVisibleItemsChanged={{this.updateVisibleItems}}
			as |_visibleItems loadNext|
		>
			<div
				class={{this.classNames}}
				{{didInsert (fn this.setupViewWithLoadNext loadNext)}}
				{{willDestroy this.teardownView}}
				{{onWindowEvents this.updateGridOptions "resize" 200}}
			>
				{{yield
					this.yieldedItems
					(hash
						loadNext=this.loadNext
						itemsPerRow=this.itemsPerRow
						hideSort=this.hideSort
						isNextLoadFinal=this.isNextLoadFinal
						areAllItemsLoaded=this.areAllItemsLoaded
					)
				}}
			</div>
		</CommonListLoadNextComponent>
	</template>
}

export default class CommonListLazyLoadComponent<
	FS extends LazyLoadFilterSort,
> extends CommonListLazyLoadBaseComponent<FS, object> {}
